import { api } from './apiClient';

/**
 * Budget Control Center API. All figures are computed server-side from REAL
 * posted journals (actuals) and stored budget lines — never mocked. The FE
 * apiClient auto-unwraps the { status, data } envelope, so these resolve to
 * the inner payloads directly.
 */

export type BudgetStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'ACTIVE' | 'LOCKED' | 'CLOSED' | 'REJECTED';
export type GroupBy = 'account' | 'costCenter' | 'department' | 'project';

export interface BudgetListItem {
  id: number; name: string; financialYear: string; status: BudgetStatus;
  budgetType?: string; owner?: string | null; version?: number; notes?: string | null;
}

export interface BudgetSummary {
  totalBudget: number; actual: number; remaining: number; utilisation: number | null;
  forecastAtCompletion: number; variance: number; activeCount: number;
  byType: { type: string; budget: number; actual: number; count: number }[];
  exceptions: { title: string; detail: string; severity: 'critical' | 'warning' | 'info'; amount: number; budgetId: number; accountId: number | null }[];
}

export interface VsActualRow {
  key: number | null; code: string | null; name: string | null; groupBy: GroupBy; accountType: string;
  budget: number; actual: number; remaining: number; variance: number;
  variancePct: number | null; utilisation: number | null; forecast: number;
  favourable: boolean; varianceLabel: string; monthlyBudget: number[]; monthlyActual: number[];
  accountId?: number; accountCode?: string; accountName?: string;
}

export interface VsActual {
  budget: { id: number; name: string; financialYear: string; status: BudgetStatus; budgetType?: string; owner?: string | null; version?: number };
  rows: VsActualRow[];
  totals: { budget: number; actual: number; remaining: number; variance: number; forecast: number; monthlyBudget: number[]; monthlyActual: number[] };
  groupBy: GroupBy; elapsedFraction: number;
}

export interface Budget360 {
  header: {
    id: number; name: string; financialYear: string; status: BudgetStatus; budgetType: string;
    owner: string | null; company: string | null; plant: string | null; version: number; notes: string | null;
    totalBudget: number; actual: number; remaining: number; utilisation: number | null;
    forecast: number; variance: number; elapsedFraction: number;
  };
  lines: any[];
  accounts: VsActualRow[];
  totals: VsActual['totals'];
  monthly: { month: string; budget: number; actual: number }[];
  versions: { id: number; version: number; reason: string | null; total_amount: number; created_by: number | null; created_at: string; createdByName: string | null }[];
  audit: { createdBy: number | null; createdByName: string | null; createdAt: string | null; approvedBy: number | null; approvedByName: string | null; approvedAt: string | null; versionsCount: number };
}

export interface BudgetLineInput {
  accountId: number; amount: number; periodNumber?: number | null;
  costCenterId?: number | null; departmentId?: number | null; projectId?: number | null; profitCenterId?: number | null;
}

export interface CreateBudgetInput {
  name: string; financialYear: string; budgetType?: string; owner?: string; company?: string; plant?: string;
  notes?: string; status?: BudgetStatus; lines: BudgetLineInput[];
}

export const budgetService = {
  list: (): Promise<{ budgets: BudgetListItem[] }> => api.get('/budgets'),
  summary: (): Promise<BudgetSummary> => api.get('/budgets/summary'),
  get: (id: number): Promise<{ budget: any }> => api.get(`/budgets/${id}`),
  budget360: (id: number): Promise<Budget360> => api.get(`/budgets/${id}/360`),
  vsActual: (id: number, groupBy: GroupBy = 'account', monthly = true): Promise<VsActual> =>
    api.get(`/budgets/${id}/vs-actual`, { params: { groupBy, monthly } }),
  create: (d: CreateBudgetInput): Promise<{ budget: any }> => api.post('/budgets', d),
  update: (id: number, d: Partial<CreateBudgetInput> & { status?: BudgetStatus }): Promise<{ budget: any }> => api.put(`/budgets/${id}`, d),
  copy: (id: number, d: { targetFinancialYear: string; growthPct?: number; name?: string }): Promise<{ budget: any }> => api.post(`/budgets/${id}/copy`, d),
};

export default budgetService;
